import { Directive } from '@angular/core';
import {
  IMasterDataChange,
  IMasterdataChangeRequestResponseBean,
  IMasterdataChangeTicket,
  ImmomioIconName,
  MasterdataChangeRequestType,
  MasterdataChangeType,
  masterDataSelectionTranslateKeys,
  ScoreQuestionType,
} from '../../models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import * as fromMasterDataState from '../../+state';
import {
  getSendMasterDataChangeRequestActionState,
  getSendMasterDataChangeTicketActionState,
  IMasterDataState,
  SendMasterDataChangeRequest,
  SendMasterDataChangeTicket,
} from '../../+state';
import { filter, map, Observable, take, tap } from 'rxjs';
import { RouterService, ticketConverter } from '../../services';
import { normalizeDateToISOString } from '../../utils';

@UntilDestroy()
@Directive()
export abstract class UserEditOverviewBaseComponent {
  public masterDataChange$: Observable<IMasterDataChange | null>;
  public masterDataChangeRequestResponse$: Observable<IMasterdataChangeRequestResponseBean>;
  public masterDataChangeTicketResponse$: Observable<boolean | null>;
  public masterDataChangeType: MasterdataChangeType;
  public displayDate = new Date();
  public immomioIconName = ImmomioIconName;
  public masterDataSelectionTranslateKeys = masterDataSelectionTranslateKeys;
  public scoreQuestionType = ScoreQuestionType;
  public masterDataChangeRequestActionState$ = this.masterDataStore.select(
    getSendMasterDataChangeRequestActionState
  );
  public masterDataChangeTicketActionState$ = this.masterDataStore.select(
    getSendMasterDataChangeTicketActionState
  );
  protected readonly MasterdataChangeType = MasterdataChangeType;

  constructor(
    protected masterDataStore: Store<IMasterDataState>,
    protected routerService: RouterService
  ) {
    this.masterDataChange$ = this.masterDataStore
      .select(fromMasterDataState.getMasterDataChange)
      .pipe(
        take(1),
        map(masterDataChange => masterDataChange),
        tap(result => {
          this.masterDataChangeType = result.selectedChangeConfiguration.changeType;
        })
      );

    this.masterDataChangeRequestResponse$ = this.masterDataStore
      .select(fromMasterDataState.getSendMasterDataChangeRequestResponse)
      .pipe(
        untilDestroyed(this),
        filter(success => success !== null)
      );

    this.masterDataChangeTicketResponse$ = this.masterDataStore
      .select(fromMasterDataState.getSendMasterDataChangeTicketResponse)
      .pipe(
        untilDestroyed(this),
        filter((success): success is boolean => success !== null)
      );
  }

  // This checks if the boolean value was true and if there is a resonse then checks if all contracts were successful.

  public createMasterDataChangeRequest() {
    this.masterDataChange$.pipe(take(1)).subscribe(change => {
      const requestType = change.selectedChangeConfiguration.requestType;
      if (requestType === MasterdataChangeRequestType.EXTERNAL) {
        let payload = JSON.parse(JSON.stringify(change.masterDataChangeRequest));

        // Handle date-of-birth timezone normalization
        if (
          payload?.type === MasterdataChangeType.DATE_OF_BIRTH &&
          payload?.dateOfBirth?.dateOfBirth
        ) {
          const dateValue = payload.dateOfBirth.dateOfBirth;
          // Normalize date to prevent timezone shifts that could change the selected date
          if (
            dateValue instanceof Date ||
            typeof dateValue === 'string' ||
            typeof dateValue === 'number'
          ) {
            payload.dateOfBirth.dateOfBirth = normalizeDateToISOString(dateValue);
          }
        }

        if (payload?.payment?.selectedContracts) {
          const { payment, ...restPayload } = payload;

          const { selectedContracts, ...restPaymentPayload } = payment;

          payload = {
            payment: {
              ...restPaymentPayload,
            },
            ...restPayload,
          };
        }

        this.masterDataStore.dispatch(
          SendMasterDataChangeRequest({
            payload,
          })
        );
      } else if (requestType === MasterdataChangeRequestType.TICKET) {
        const ticketChangePayload: IMasterdataChangeTicket = {
          formResponses: ticketConverter.formatFormResponses(change.ticketCreationSummary),
          ticketCategoryId: change.selectedChangeConfiguration.ticketCategoryId,
        };
        this.masterDataStore.dispatch(SendMasterDataChangeTicket({ payload: ticketChangePayload }));
      }
    });
  }

  protected parseResponse(response: boolean | IMasterdataChangeRequestResponseBean): boolean {
    if (typeof response === 'boolean') {
      return response;
    }

    return response.changeRequestStatuses?.every(status => status.success) ?? false;
  }
}
